import BookingsCountLabel from 'components/Common/Labels/BookingsCountLabel'
import RoomsRemainingLabel from 'components/Common/Labels/RoomsRemainingLabel'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import Pane from 'components/Common/Pane'
import ScrollAnchor from 'components/Common/ScrollAnchor'
import MessageBanner from 'components/Luxkit/Banners/MessageBanner'
import SmallToggleButton from 'components/Luxkit/Button/SmallToggleButton'
import LineInfoCircleIcon from 'components/Luxkit/Icons/line/LineInfoCircleIcon'
import LineSightseeingIcon from 'components/Luxkit/Icons/line/LineSightseeingIcon'
import BodyText from 'components/Luxkit/Typography/BodyText'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Plural from 'components/utils/Formatters/Plural'
import { OfferPageActions, OfferPageFilterType } from 'contexts/OfferPage/offerPageStateReducer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import useLPCUniqueOptions from 'hooks/OfferPage/useLPCUniqueOptions'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import useQueryParams from 'hooks/useQueryParams'
import useToggle from 'hooks/useToggle'
import { take } from 'lib/array/arrayUtils'
import { initialRooms } from 'lib/offer/occupancyUtils'
import { isHotelPackage } from 'lib/offer/offerTypes'
import uuidV4 from 'lib/string/uuidV4Utils'
import { rem } from 'polished'
import React, { MouseEventHandler, useCallback, useEffect, useMemo } from 'react'
import { arePricesLoading, getCheckoutDate, isAvailableForPurchase, OfferPackageOptionView } from 'selectors/offerPage/offerPageSelectors'
import styled from 'styled-components'
import BookmarkButton from 'tripPlanner/components/Bookmark/BookmarkButton/BookmarkButton'
import TripGuard from 'tripPlanner/components/TripGuard/TripGuard'
import AccommodationRoomTile from '../Accommodation/AccommodationRoomTile'
import AccommodationRoomTileActions, { AccommodationRoomTileActionsState } from '../Accommodation/AccommodationRoomTileActions'
import AccommodationRoomTileDetails from '../Accommodation/AccommodationRoomTileDetails'
import AccommodationRoomTileImage from '../Accommodation/AccommodationRoomTileImage'
import AlwaysOnRateOption from '../AlwaysOn/AlwaysOnRateOption'
import BedbankRoomOptionRate from '../Bedbank/BedbankRoomOptionRate'
import { getCombinedRates, getRateListKey, isRoomRateUnmatched } from './singlePropertyUtils'
import usePropertyRatesList from 'hooks/usePropertyRatesList'
import { OptimizelyFeatureFlags } from 'constants/optimizely'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'
import { preventRenderWithOpenFilter } from 'lib/offer/offerPageUtil'

const RateSection = styled.div`
  > * {
    border-top: 1px solid ${props => props.theme.palette.neutral.default.five};
  }
`

const ShowMoreContent = styled(BodyText)`
  padding: ${rem(12)};
`

interface Props {
  offer: App.Offer;
  bedbankOffer?: App.BedbankOffer;
  packageViews: Array<OfferPackageOptionView>;
  bedbankViews: Array<App.BedbankOfferPackageView>;
  checkIn?: string;
  checkOut?: string;
  rooms: Array<App.Occupants>;
  flightDestination?: App.Airport;
  previewMode: boolean;
  isHighlighted?: boolean;
  onViewPackageOptions: () => void;
  roomRateId?: string;
  pricesLoading?: boolean;
  duration?: number;
  fetchingBedbankViews?: boolean;
  airport?: App.Airport;
  roomListId: string;
  availableForPurchase?: boolean;
}

function PropertyRoomOption(props: Props) {
  const {
    offer,
    bedbankOffer,
    packageViews,
    fetchingBedbankViews,
    checkIn,
    checkOut,
    rooms,
    flightDestination,
    previewMode,
    isHighlighted,
    onViewPackageOptions,
    roomRateId,
    pricesLoading,
    duration,
    bedbankViews,
    roomListId,
    availableForPurchase,
  } = props

  const utmContent = useQueryParams().get('utm_content') || undefined

  // trust me bro, it's a hotel package
  const pkg =
    (packageViews[0]?.package ?? bedbankViews[0].package) as (App.HotelPackage | App.BedbankPackage)
  const isLpcPackage = isHotelPackage(pkg)
  const opaqueRoomId = isLpcPackage ? pkg.roomType.id : pkg.roomId
  const roomType = isLpcPackage ? pkg.roomType : undefined
  const [showingMoreRates, showMoreRates] = useToggle()
  const offerPageDispatch = useOfferPageDispatch()
  const initialRatesToShow = isHighlighted ? 1 : 3

  const [roomFitsCapacity, datesAvailable] = useMemo(() => [
    packageViews.some(view => view.capacityValid) || bedbankViews.some(view => view.capacityValid),
    packageViews.some(view => view.hotelAvailable) || bedbankViews.some(view => view.available),
  ], [packageViews, bedbankViews])

  const options = useMemo(() => {
    if (previewMode) {
      // Preview mode is for content people and will show unavailable rates so they can validate them
      // as the rates may not have had prices and such loaded yet
      return packageViews
    }
    const requiresRates = offer.property?.useDynamicCancellationPolicies && !!checkIn && !pricesLoading
    return packageViews.filter(view => view.capacityValid && view.hotelAvailable && (!requiresRates || view.hasAvailableRates))
  }, [checkIn, offer, packageViews, previewMode, pricesLoading])

  const useRatesEndpoint = useOptimizelyExperiment(OptimizelyFeatureFlags.accomUsePropertyPageRatesEndpoint)
  const availableOptions = useLPCUniqueOptions(
    offer.id,
    options,
    {
      useDynamicCancellationPolicies: offer.property?.useDynamicCancellationPolicies ?? false,
      duration,
      checkIn,
      rooms: rooms ?? initialRooms,
      showAll: useRatesEndpoint,
    },
  )

  const roomRateUnmatched = useMemo(() => isRoomRateUnmatched(roomRateId, { availableOptions, isHighlighted, utmContent }), [availableOptions, isHighlighted, roomRateId, utmContent])

  useEffect(() => {
    if (isHighlighted) {
      onViewPackageOptions()
    }
  }, [isHighlighted]) // eslint-disable-line react-hooks/exhaustive-deps

  const checkAvailability = !checkIn || !rooms

  const actionsState = useMemo<AccommodationRoomTileActionsState | undefined>(() => {
    if (!availableForPurchase) return 'sold-out'

    if (availableForPurchase) {
      if (!offer.property?.useDynamicOccupancy) {
        if (!datesAvailable) return 'sold-out-on-dates'
        if (!roomFitsCapacity && datesAvailable) return 'exceeded-capacity'
        if (checkAvailability) {
          if (roomFitsCapacity && datesAvailable) return 'available-on-dates'
          if (!roomFitsCapacity && datesAvailable) return 'exceeded-capacity'
        }
      }
      if (offer.property?.useDynamicOccupancy) {
        if (!checkIn) return 'available-on-dates'
        if (!datesAvailable) return 'sold-out-on-dates-or-exceeded-capacity'
      }
    }
  }, [availableForPurchase, checkAvailability, checkIn, datesAvailable, offer, roomFitsCapacity])

  const stayPayTier = useMemo(() => {
    return packageViews.map(view => {
      return view.hotelAvailable && view.package?.stayPay?.find(tier => tier.stayNights === duration && tier.freeNights > 0)
    }).find(Boolean)
  }, [packageViews, duration])

  const toggleFilter = useCallback((filter: OfferPageFilterType) => {
    offerPageDispatch({ type: OfferPageActions.setRoomType, room: roomType })
    offerPageDispatch({ type: OfferPageActions.toggleFilter, filter })
  }, [offerPageDispatch, roomType])

  const handleChangeDatesClick = useCallback<MouseEventHandler<HTMLButtonElement>>(() => {
    toggleFilter('checkin')
  }, [toggleFilter])

  const handleCheckAvailabilityClick = useCallback<MouseEventHandler<HTMLButtonElement>>(() => {
    toggleFilter(rooms ? 'checkin' : 'capacity')
  }, [rooms, toggleFilter])

  const handleAddAnotherUnitClick = useCallback<MouseEventHandler<HTMLButtonElement>>(() => {
    toggleFilter('capacity')
  }, [toggleFilter])

  const [propertyRates] = usePropertyRatesList(offer.property?.id, {
    checkIn,
    checkOut,
    occupancies: rooms ?? initialRooms,
    dynamicAvailability: offer.property?.useDynamicAvailability,
    disabled: !useRatesEndpoint,
  })

  const displayRates = useMemo(() => {
    const combinedRates = getCombinedRates(
      packageViews,
      bedbankViews,
      availableOptions,
      {
        previewMode,
        showAll: useRatesEndpoint,
        roomRateId,
        isHighlighted,
        utmContent,
      },
    )

    if (useRatesEndpoint && checkIn && checkOut) {
      const rateIds = new Set(propertyRates.map(rate => rate.key))
      return combinedRates.filter(rate => rateIds.has(getRateListKey(rate)))
    }
    return combinedRates
  }, [availableOptions, bedbankViews, checkIn, checkOut, isHighlighted, packageViews, previewMode, propertyRates, roomRateId, useRatesEndpoint, utmContent])

  const rateListId = useMemo(() => uuidV4(),
  // eslint-disable-next-line react-hooks/exhaustive-deps
    [bedbankViews]) // regenerate id if packages change

  return (
    <>
      {checkIn && stayPayTier && <MessageBanner
        kind="promotional-primary"
        icon={<LineInfoCircleIcon />}
        description={<>
          <b>Special offer:</b> You get <Plural singular="night" count={stayPayTier.freeNights} withCount />{' '}
          free when staying <Plural singular="night" count={ stayPayTier.stayNights} withCount /> on select packages.
        </>}
      />}
      <Pane border="neutral-five" borderRadius="S" data-testid="package-option">
        {isHighlighted && <MessageBanner
          kind="promotional-primary"
          description={<b>You were interested in this option</b>}
          icon={!roomRateUnmatched && <LineSightseeingIcon />}
        />}
        <ScrollAnchor id={opaqueRoomId} vOffset={rem(80)} />
        <AccommodationRoomTile roomPackage={pkg}>
          <AccommodationRoomTileImage
            roomPackage={pkg}
            // Hide the bookmark button if there's no LPC package, as otherwise we'd wind up with a
            // mix of reservation _and_ bedbank room IDs. In the future, we'd want to migrate to
            // using accommodation room IDs.
            showBookmark={isLpcPackage}
          />
          <AccommodationRoomTileDetails
            roomPackage={pkg}
            urgencyLabels={<>
              {isLpcPackage && <BookingsCountLabel bookings={roomType.stats.booked} threshold="room" />}
              {checkIn && <RoomsRemainingLabel roomCount={packageViews[0]?.availableRooms ?? 0} threshold="rate" />}
            </>}
            actions={availableForPurchase && roomFitsCapacity && datesAvailable && isLpcPackage && <CSSBreakpoint min="desktop">
              <TripGuard>
                <BookmarkButton offer={offer} roomTypeId={roomType.id} />
              </TripGuard>
            </CSSBreakpoint>}
          >
            <AccommodationRoomTileActions
              roomPackage={pkg}
              state={actionsState}
              onAddAnotherUnitClick={handleAddAnotherUnitClick}
              onChangeDatesClick={handleChangeDatesClick}
              onCheckAvailabilityClick={handleCheckAvailabilityClick}
            />
          </AccommodationRoomTileDetails>
        </AccommodationRoomTile>
        <RateSection data-testid="rate-options">
          {take(displayRates, showingMoreRates ? 100 : initialRatesToShow).map((view, index) => {
            if (view.lpcRate) {
              return <AlwaysOnRateOption
                key={view.lpcRate.package.uniqueKey}
                offer={offer}
                optionNumber={index + 1}
                packageView={view.lpcRate}
                flightDestination={flightDestination}
                showOptionLabel={displayRates.length > 1}
              />
            } else if (view.bedbankRate && bedbankOffer) {
              return <BedbankRoomOptionRate
                key={view.bedbankRate.rate.id}
                offer={bedbankOffer}
                packageView={view.bedbankRate.view}
                rate={view.bedbankRate.rate}
                optionNumber={index + 1}
                roomPosition={index + 1}
                roomListId={roomListId}
                rateListId={rateListId}
                showOptionLabel={displayRates.length > 1}
              />
            }
            return null
          })}
          {fetchingBedbankViews && <LoadingIndicator inline />}
          {displayRates.length > initialRatesToShow && <ShowMoreContent variant="medium" align="center">
            <SmallToggleButton
              on={showingMoreRates}
              onClick={showMoreRates}
            >
              {showingMoreRates ? 'Show less options' : 'Show more options'}
            </SmallToggleButton>
          </ShowMoreContent>}
        </RateSection>
      </Pane>
    </>
  )
}

export default withOfferPageAndReduxState((state, appState) => ({
  rooms: state.rooms,
  checkIn: state.checkInDate,
  checkOut: getCheckoutDate(state),
  duration: state.duration,
  previewMode: appState.system.previewMode,
  roomRateId: state.roomOption?.roomRateId,
  pricesLoading: arePricesLoading(appState, state),
  airport: state.airport,
  availableForPurchase: isAvailableForPurchase(state, appState),
  open: !!state.activeFilter,
}), PropertyRoomOption, preventRenderWithOpenFilter)
